/**
 * MFA enrollment screen (Req 22.7, 22.11).
 *
 * Presents second-factor set-up: an enrollment prompt with a QR placeholder
 * (the secret/QR is supplied by the Identity_Service; the screen shows a
 * placeholder region for it) and a code field whose value submits to the
 * Identity_Service to confirm enrollment. On success it shows an accessible
 * success {@link UserMessage} (status severity, not an error).
 *
 * Web boundary: the Identity_Service enrollment confirmation is injected as
 * `onEnroll`; the screen never imports backend runtime code. The registry
 * renders it with no props for SSR, so the prop is optional and the form renders
 * standalone.
 *
 * All text resolves through `useT()` (Req 22.11). SSR-friendly: the first render
 * is a pure function of props; interactivity activates on hydration.
 */
import { useState, type FormEvent, type ReactElement } from "react";
import { useT } from "../i18n/index.js";
import {
  UserMessage,
  type UserMessageModel,
} from "../components/UserMessage.js";

/**
 * The outcome of confirming enrollment. `ok` enables the second factor and
 * shows the success message; `invalid` leaves the field editable to retry.
 */
export type MfaEnrollmentResult = "ok" | "invalid";

/** Props for {@link MfaEnrollmentScreen}. All optional so it renders standalone. */
export interface MfaEnrollmentScreenProps {
  /**
   * Injected Identity_Service enrollment confirmation. Receives the entered
   * code and resolves to a {@link MfaEnrollmentResult}. Omitted in SSR / the
   * registry, where the form renders but does not submit.
   */
  readonly onEnroll?: (code: string) => Promise<MfaEnrollmentResult>;
  /**
   * A URI/data string for the authenticator QR code, supplied by the
   * Identity_Service. When absent the screen renders a QR placeholder region so
   * the layout is stable (and SSR has real content).
   */
  readonly qrCodeUri?: string;
  /** An initial result to render on first paint. */
  readonly initialResult?: MfaEnrollmentResult;
}

export default function MfaEnrollmentScreen({
  onEnroll,
  qrCodeUri,
  initialResult,
}: MfaEnrollmentScreenProps = {}): ReactElement {
  const t = useT();
  const [code, setCode] = useState("");
  const [result, setResult] = useState<MfaEnrollmentResult | undefined>(
    initialResult,
  );

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (onEnroll === undefined) {
      return;
    }
    const outcome = await onEnroll(code);
    setResult(outcome);
  }

  const successMessage = toMfaEnrollmentSuccessMessage(result, t);

  return (
    <section aria-labelledby="screen-heading" data-screen="mfaEnrollment">
      <h1 id="screen-heading">{t("auth.mfaEnroll.title")}</h1>
      <p>{t("auth.mfaEnroll.prompt")}</p>

      {/*
        QR placeholder region. When the Identity_Service supplies a QR URI it is
        shown; otherwise a stable placeholder keeps the layout intact and gives
        SSR real content. Marked with a role/label so assistive technology can
        find it.
      */}
      <div
        className="mfa-enroll__qr"
        data-testid="mfa-enroll-qr"
        role="img"
        aria-label={t("auth.mfaEnroll.prompt")}
      >
        {qrCodeUri !== undefined ? (
          <img src={qrCodeUri} alt={t("auth.mfaEnroll.prompt")} />
        ) : (
          <span className="mfa-enroll__qr-placeholder" aria-hidden="true">
            ▢
          </span>
        )}
      </div>

      {successMessage !== undefined ? (
        <UserMessage message={successMessage} severity="status" />
      ) : null}

      <form onSubmit={handleSubmit} noValidate data-testid="mfa-enroll-form">
        <div className="field">
          <label htmlFor="mfa-enroll-code">{t("auth.mfaEnroll.codeLabel")}</label>
          <input
            id="mfa-enroll-code"
            name="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e): void => setCode(e.target.value)}
          />
        </div>

        <button type="submit" data-testid="mfa-enroll-submit">
          {t("auth.mfaEnroll.submit")}
        </button>
      </form>
    </section>
  );
}

/**
 * Build the success {@link UserMessageModel} for a completed enrollment by
 * resolving the catalog ids (what/why/next). Returns `undefined` unless the
 * result is `"ok"`. Pure and SSR-safe.
 */
export function toMfaEnrollmentSuccessMessage(
  result: MfaEnrollmentResult | undefined,
  t: (id: string) => string,
): UserMessageModel | undefined {
  if (result !== "ok") {
    return undefined;
  }
  return {
    what: t("auth.mfaEnroll.success.what"),
    why: t("auth.mfaEnroll.success.why"),
    next: t("auth.mfaEnroll.success.next"),
  };
}
